import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createClient, getScopePermissions } from 'utils/Clients';

interface CreateClientFormDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated: (result: ClientCreateResult) => void;
}

const GRANT_OPTIONS: Array<{ value: string; label: string; description: string }> = [
    {
        value: 'authorization_code',
        label: 'authorization_code',
        description: 'Flujo para aplicaciones web con redirección y código temporal.',
    },
    {
        value: 'password',
        label: 'password',
        description: 'Flujo legado con usuario/clave; usar solo en escenarios controlados.',
    },
    {
        value: 'client_credentials',
        label: 'client_credentials',
        description: 'Flujo máquina-a-máquina sin usuario final.',
    },
    {
        value: 'refresh_token',
        label: 'refresh_token',
        description: 'Permite renovar access tokens sin volver a autenticar al usuario.',
    },
];

const CreateClientFormDialog: React.FC<CreateClientFormDialogProps> = ({ open, onClose, onCreated }) => {
    const [name, setName] = useState('');
    const [redirectUri, setRedirectUri] = useState('');
    const [selectedGrants, setSelectedGrants] = useState<Set<string>>(new Set());
    const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [scopeFilter, setScopeFilter] = useState('');
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const groupedPermissions = useMemo(() => {
        const groups = new Map<string, Permission[]>();

        permissions.forEach((permission) => {
            const permissionName = permission.name;
            const [group] = permissionName.split('.');
            if (!groups.has(group)) {
                groups.set(group, []);
            }
            groups.get(group)?.push(permission);
        });

        return Array.from(groups.entries())
            .map(([group, items]) => ({
                group,
                groupDescription: permissions.find((permission) => permission.name === group)?.description || '',
                items: Array.from(
                    new Map(items.map((permission) => [permission.name, permission])).values(),
                ).sort((a, b) => a.name.localeCompare(b.name)),
            }))
            .sort((a, b) => a.group.localeCompare(b.group));
    }, [permissions]);

    const filteredGroupedPermissions = useMemo(() => {
        const normalizedTerms = scopeFilter
            .toLowerCase()
            .split(/\s+/)
            .map((term) => term.trim())
            .filter(Boolean);

        if (normalizedTerms.length === 0) {
            return groupedPermissions;
        }

        const matchesTerms = (text: string) => {
            const normalizedText = text.toLowerCase();
            return normalizedTerms.every((term) => normalizedText.includes(term));
        };

        return groupedPermissions
            .map(({ group, groupDescription, items }) => {
                const filteredItems = items.filter((permission) =>
                    matchesTerms(`${permission.name} ${permission.description || ''}`),
                );

                const groupMatches = matchesTerms(`${group} ${groupDescription || ''}`);

                return {
                    group,
                    groupDescription,
                    items: filteredItems,
                    includeGroup: groupMatches || filteredItems.length > 0,
                };
            })
            .filter(({ includeGroup }) => includeGroup)
            .map(({ includeGroup, ...groupData }) => groupData);
    }, [groupedPermissions, scopeFilter]);

    const resetForm = () => {
        setName('');
        setRedirectUri('');
        setSelectedGrants(new Set());
        setSelectedScopes(new Set());
        setScopeFilter('');
        setErrorMessage('');
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        let isMounted = true;

        const loadPermissions = async () => {
            try {
                setLoadingPermissions(true);
                const data = await getScopePermissions();
                if (!isMounted) {
                    return;
                }
                setPermissions(data ?? []);

                setExpandedGroups(new Set());
            } catch (error: any) {
                if (isMounted) {
                    setErrorMessage(error?.message || 'No fue posible obtener los permisos para scopes.');
                }
            } finally {
                if (isMounted) {
                    setLoadingPermissions(false);
                }
            }
        };

        loadPermissions();

        return () => {
            isMounted = false;
        };
    }, [open]);

    const toggleGrant = (grant: string) => {
        setSelectedGrants((previous) => {
            const next = new Set(previous);
            if (next.has(grant)) {
                next.delete(grant);
            } else {
                next.add(grant);
            }
            return next;
        });
    };

    const toggleScope = (scope: string) => {
        setSelectedScopes((previous) => {
            const next = new Set(previous);
            if (next.has(scope)) {
                next.delete(scope);
            } else {
                next.add(scope);
            }
            return next;
        });
    };

    const toggleScopeGroup = (group: string, items: Permission[]) => {
        setSelectedScopes((previous) => {
            const next = new Set(previous);
            const isSelected = next.has(group);

            if (isSelected) {
                next.delete(group);
            } else {
                next.add(group);
                items.forEach((permission) => next.delete(permission.name));
            }

            return next;
        });

        setExpandedGroups((previous) => {
            const next = new Set(previous);
            next.delete(group);
            return next;
        });
    };

    const handleSubmit = async () => {
        setErrorMessage('');

        if (!name.trim()) {
            setErrorMessage('El nombre del cliente es obligatorio.');
            return;
        }

        if (selectedGrants.size === 0) {
            setErrorMessage('Debes seleccionar al menos un grant.');
            return;
        }

        if (selectedScopes.size === 0) {
            setErrorMessage('Debes seleccionar al menos un scope.');
            return;
        }

        try {
            setSubmitting(true);
            const result = await createClient({
                name: name.trim(),
                redirect_uri: redirectUri.trim() || undefined,
                grant_types: Array.from(selectedGrants),
                scope: Array.from(selectedScopes),
            });

            onCreated(result);
            resetForm();
            onClose();
        } catch (error: any) {
            setErrorMessage(error?.message || 'No fue posible crear el cliente.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Crear cliente</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label='Nombre'
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        fullWidth
                        size='small'
                    />

                    <TextField
                        label='Redirect URI'
                        value={redirectUri}
                        onChange={(event) => setRedirectUri(event.target.value)}
                        fullWidth
                        size='small'
                        helperText='Para múltiples URLs, sepáralas por coma.'
                    />

                    <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Grants</Typography>
                        <FormGroup
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                columnGap: 2,
                            }}
                        >
                            {GRANT_OPTIONS.map((option) => (
                                <Tooltip key={option.value} title={option.description} placement='right'>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedGrants.has(option.value)}
                                                onChange={() => toggleGrant(option.value)}
                                            />
                                        }
                                        label={option.label}
                                    />
                                </Tooltip>
                            ))}
                        </FormGroup>
                    </Box>

                    <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Scopes</Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                            Los scopes definen los alcances y acciones que el cliente puede solicitar al servicio de autenticacion.
                        </Typography>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Permisos detallados</Typography>
                        <TextField
                            placeholder='Filtrar permisos...'
                            value={scopeFilter}
                            onChange={(event) => setScopeFilter(event.target.value)}
                            fullWidth
                            size='small'
                            sx={{ mb: 1 }}
                        />
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'grid' },
                                gridTemplateColumns: 'minmax(0, 380px) minmax(0, 1fr)',
                                columnGap: 2,
                                px: 2,
                                pb: 1,
                            }}
                        >
                            <Typography variant='caption' color='text.secondary'>Permiso</Typography>
                            <Typography variant='caption' color='text.secondary'>Descripcion</Typography>
                        </Box>
                        {loadingPermissions ? (
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <CircularProgress size={18} />
                                <Typography variant='body2'>Cargando permisos...</Typography>
                            </Stack>
                        ) : (
                            <>
                                {filteredGroupedPermissions.map(({ group, groupDescription, items }) => {
                                    const isGroupSelected = selectedScopes.has(group);
                                    const isExpanded = expandedGroups.has(group);
                                    const isFiltering = scopeFilter.trim().length > 0;

                                    return (
                                        <Accordion
                                            key={group}
                                            disableGutters
                                            expanded={!isGroupSelected && (isFiltering || isExpanded)}
                                            onChange={(_, expanded) => {
                                                if (isGroupSelected) {
                                                    return;
                                                }
                                                setExpandedGroups((previous) => {
                                                    const next = new Set(previous);
                                                    if (expanded) {
                                                        next.add(group);
                                                    } else {
                                                        next.delete(group);
                                                    }
                                                    return next;
                                                });
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isGroupSelected}
                                                            onClick={(event) => event.stopPropagation()}
                                                            onChange={() => toggleScopeGroup(group, items)}
                                                        />
                                                    }
                                                    label={
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                display: 'grid',
                                                                gridTemplateColumns: {
                                                                    xs: '1fr',
                                                                    md: 'minmax(0, 380px) minmax(0, 1fr)',
                                                                },
                                                                columnGap: 2,
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Typography variant='body1' sx={{ overflowWrap: 'anywhere' }}>{group}</Typography>
                                                            <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}>
                                                                {groupDescription || '-'}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{ width: '100%', mr: 0 }}
                                                    onClick={(event) => event.stopPropagation()}
                                                />
                                            </AccordionSummary>

                                            <AccordionDetails>
                                                <FormGroup>
                                                    {items.map((permission) => (
                                                        <FormControlLabel
                                                            key={permission.name}
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedScopes.has(permission.name)}
                                                                    disabled={isGroupSelected}
                                                                    onChange={() => toggleScope(permission.name)}
                                                                />
                                                            }
                                                            label={
                                                                <Box
                                                                    sx={{
                                                                        width: '100%',
                                                                        display: 'grid',
                                                                        gridTemplateColumns: {
                                                                            xs: '1fr',
                                                                            md: 'minmax(0, 380px) minmax(0, 1fr)',
                                                                        },
                                                                        columnGap: 2,
                                                                        alignItems: 'start',
                                                                    }}
                                                                >
                                                                    <Typography variant='body1' sx={{ overflowWrap: 'anywhere' }}>
                                                                        {permission.name}
                                                                    </Typography>
                                                                    <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}>
                                                                        {permission.description || '-'}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            sx={{ width: '100%', mr: 0 }}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                                <Typography variant='body2' color='text.secondary' sx={{ mt: 1, textAlign: 'right' }}>
                                    Permisos seleccionados: {selectedScopes.size}
                                </Typography>
                            </>
                        )}
                    </Box>

                    {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                <Button onClick={handleSubmit} variant='contained' disabled={submitting || loadingPermissions}>
                    Crear
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { CreateClientFormDialog };
